import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './TransactionsSection.styles';
import { Operation } from './Transactions.types';
import { useOperations } from './hooks';
import { OperationDetailsBottomSheet } from './components/OperationDetailsBottomSheet';
import { OperationDetailsModal } from './components/OperationDetailsModal';
import { OperationsEmptyState } from './components/OperationsEmptyState';
import { OperationsList } from './components/OperationsList';
import { OperationsListSkeleton } from './components/OperationsListSkeleton';
import { OperationsTable } from './components/OperationsTable';
import { OperationsTableSkeleton } from './components/OperationsTableSkeleton';
import { TransactionsTabs } from './components/TransactionsTabs';
import { getActiveTab } from './Transactions.helpers';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { useResponsive } from '@/hooks/useResponsive';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { useCurrentUser } from '@/features/Profile/core/hooks';
import { getRouteHash } from '@/hooks/useToggleOpenState';
import { QUERY } from '@/constants/common';
import { useFreezeProfileSidebarWhile } from '@/features/Profile/core/ProfileLayout/ProfileLayout.hooks';

export const TransactionsSection = () => {
  const { t } = useTranslation();
  const { hash } = useUrlAndHashFromPath();
  const { isDesktop } = useResponsive();
  const { isShop } = useCurrentUser();
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(
    null
  );
  const routeHash = getRouteHash(hash, QUERY.replenishment);
  const activeTab = getActiveTab(routeHash, isShop);
  const {
    operations,
    isLoading,
    isLoadingMore,
    hasNextPage,
    canLoadMore,
    loadMore,
  } = useOperations(activeTab);
  useFreezeProfileSidebarWhile(isDesktop && isLoading);

  const isEmpty =
    !isLoading && !isLoadingMore && operations.length === 0 && !hasNextPage;

  function handleShowDetails(operation: Operation) {
    setSelectedOperation(operation);
  }

  function handleCloseDetails() {
    setSelectedOperation(null);
  }

  return (
    <S.Container>
      <S.Title>{t('pages.cabinet.operations_history')}</S.Title>
      <TransactionsTabs activeTab={activeTab} isShop={isShop} />

      <S.DesktopOnly>
        {isLoading ? (
          <OperationsTableSkeleton />
        ) : isEmpty ? (
          <OperationsEmptyState activeTab={activeTab} />
        ) : (
          <OperationsTable
            activeTab={activeTab}
            operations={operations}
            onShowDetails={handleShowDetails}
            loadMoreSkeleton={
              isLoadingMore ? (
                <OperationsTableSkeleton
                  rowsCount={4}
                  hasHeader={false}
                  hasContainer={false}
                />
              ) : null
            }
          />
        )}
      </S.DesktopOnly>

      <S.MobileOnly>
        {isLoading ? (
          <OperationsListSkeleton />
        ) : isEmpty ? (
          <OperationsEmptyState activeTab={activeTab} />
        ) : (
          <OperationsList
            operations={operations}
            onShowDetails={handleShowDetails}
            loadMoreSkeleton={
              isLoadingMore ? (
                <OperationsListSkeleton itemsCount={4} hasContainer={false} />
              ) : null
            }
          />
        )}
      </S.MobileOnly>

      {!isLoading && !isLoadingMore && canLoadMore && (
        <InfiniteLoader onVisit={loadMore} />
      )}

      {isDesktop ? (
        <OperationDetailsModal
          operation={selectedOperation}
          onClose={handleCloseDetails}
        />
      ) : (
        <OperationDetailsBottomSheet
          operation={selectedOperation}
          onClose={handleCloseDetails}
        />
      )}
    </S.Container>
  );
};
